import { BlobClient } from '@frontend/blob/api';
import { NotificationChannel } from '@frontend/common';
import { buildPreviewDocument, isFullHtmlDocument } from '@frontend/html-template-editor';
import { Notification } from '@frontend/notification/types';
import { type SyntheticEvent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

const OVERFLOW_LOCK_STYLE = '<style data-notification-preview-overflow>html,body{margin:0;overflow:hidden!important;}</style>';

const normalizeCid = (cid: string): string => cid.trim().replace(/^cid:/i, '').replace(/^<|>$/g, '');

const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob'));
        reader.readAsDataURL(blob);
    });

const rewriteCidImages = (body: string, cidToDataUrl: Map<string, string>): string => {
    const parser = new DOMParser();
    const document = parser.parseFromString(body, 'text/html');
    const images = Array.from(document.querySelectorAll('img[src]'));

    for (const image of images) {
        const source = image.getAttribute('src');
        if (!source?.toLowerCase().startsWith('cid:')) continue;

        const resolvedSource = cidToDataUrl.get(normalizeCid(source));
        if (resolvedSource) image.setAttribute('src', resolvedSource);
    }

    if (isFullHtmlDocument(body)) {
        return `<!DOCTYPE html>${document.documentElement.outerHTML}`;
    }

    return document.body.innerHTML;
};

/** Keep overflow on the parent modal/card — the iframe must never become a nested scroller. */
const buildNotificationPreviewDocument = (body: string, plainText: boolean): string => {
    const document = buildPreviewDocument(body, plainText);
    if (document.includes('</head>')) {
        return document.replace('</head>', `${OVERFLOW_LOCK_STYLE}</head>`);
    }
    if (document.includes('<body')) {
        return document.replace(/<body([^>]*)>/i, `<head>${OVERFLOW_LOCK_STYLE}</head><body$1>`);
    }
    return document;
};

const measureDocumentHeight = (document: Document): number =>
    Math.max(document.documentElement.scrollHeight, document.documentElement.offsetHeight, document.body?.scrollHeight ?? 0, document.body?.offsetHeight ?? 0);

const fitIframeToContent = (iframe: HTMLIFrameElement): void => {
    const document = iframe.contentDocument;
    if (!document?.documentElement) return;

    // Collapse first so scrollHeight reflects content, not a previous iframe viewport.
    iframe.style.height = '0px';
    iframe.style.height = `${measureDocumentHeight(document)}px`;
};

const handleIframeLoad = (event: SyntheticEvent<HTMLIFrameElement>): void => {
    const iframe = event.currentTarget;
    fitIframeToContent(iframe);

    const document = iframe.contentDocument;
    if (!document) return;

    // Second pass after layout/fonts settle.
    requestAnimationFrame(() => fitIframeToContent(iframe));

    for (const image of Array.from(document.images)) {
        if (image.complete) continue;
        image.addEventListener('load', () => fitIframeToContent(iframe), { once: true });
        image.addEventListener('error', () => fitIframeToContent(iframe), { once: true });
    }
};

export interface NotificationHtmlPreviewProps {
    body: string | null;
    blobs: Notification['blobs'];
    channel: Notification['channel'];
}

export const NotificationHtmlPreview = ({ body, blobs, channel }: NotificationHtmlPreviewProps) => {
    const intl = useIntl();
    const [resolvedBody, setResolvedBody] = useState<string | null>(body);
    const isPlainText = channel !== NotificationChannel.EMAIL;

    useEffect(() => {
        if (!body || !blobs?.length) {
            setResolvedBody(body);
            return;
        }

        let isCancelled = false;

        const resolveBody = async () => {
            try {
                const blobResults = await Promise.all(
                    blobs.map(async (blob) => {
                        try {
                            const binary = await BlobClient.fetchBlob(blob.account_id, blob.entity_type, blob.entity_id);
                            const dataUrl = await blobToDataUrl(binary);
                            return { cid: normalizeCid(blob.entity_id), dataUrl };
                        } catch {
                            return null;
                        }
                    })
                );

                const cidToDataUrl = new Map<string, string>();
                for (const result of blobResults) {
                    if (!result) continue;
                    cidToDataUrl.set(result.cid, result.dataUrl);
                }

                if (!cidToDataUrl.size) {
                    if (!isCancelled) setResolvedBody(body);
                    return;
                }

                if (!isCancelled) setResolvedBody(rewriteCidImages(body, cidToDataUrl));
            } catch {
                if (!isCancelled) setResolvedBody(body);
            }
        };

        resolveBody();

        return () => {
            isCancelled = true;
        };
    }, [body, blobs]);

    if (!resolvedBody) {
        return <div className='text-foreground-default text-base'>-</div>;
    }

    return (
        <iframe
            title={intl.formatMessage({
                id: 'notification.detail.body-iframe-title',
                description: 'Accessible title for the iframe that renders a sent notification message body',
                defaultMessage: 'Notification message body'
            })}
            className='block w-full overflow-hidden bg-white'
            sandbox='allow-same-origin allow-popups allow-popups-to-escape-sandbox'
            scrolling='no'
            srcDoc={buildNotificationPreviewDocument(resolvedBody, isPlainText)}
            onLoad={handleIframeLoad}
        />
    );
};
